import { SERVED_CLAUSES, formatUtcForPocketBase, utcFreshnessClause } from '../contracts/servedRecommendation.v1.js'

const literal = value => `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
export const CLASSIC_FIELDS = 'id,listing_id,make,model,year,mileage_km,horsepower_hp,fuel_type,transmission,car_type,country_code,price_amount_eur,source_flag,source_checked_at,source_real_url,external_link,detail_page_url,scraped_at,decision_snapshot_id,model_git_sha,prediction_status'

/** A separately named research population. Never remove safeguards from picks. */
export function classicResearchFilter({ make = '', model = '', minYear = 1990, maxYear = 1999, country = '', maxPrice = '' } = {}, now = new Date()) {
  const lo = Number(minYear), hi = Number(maxYear)
  if (!Number.isInteger(lo) || !Number.isInteger(hi) || lo < 1886 || hi > now.getUTCFullYear() - 26 || lo > hi) {
    throw new RangeError('Choose a valid classic-car year range')
  }
  const clauses = [SERVED_CLAUSES.positivePrice, SERVED_CLAUSES.noPriceAnomaly,
    SERVED_CLAUSES.releaseActive, ...SERVED_CLAUSES.queryLineage.discovery,
    SERVED_CLAUSES.notGone, "source_flag != 'sold'", utcFreshnessClause('7d', now),
    `scraped_at <= '${formatUtcForPocketBase(now)}'`,
    "(country_code = 'DE' || country_code = 'IT' || country_code = 'FR' || country_code = 'AT')",
    `year >= ${lo}`, `year <= ${hi}`]
  if (make.trim()) clauses.push(`make = ${literal(make.trim())}`)
  if (model.trim()) clauses.push(`model = ${literal(model.trim())}`)
  if (country) {
    if (!['DE', 'IT', 'FR', 'AT'].includes(country)) throw new RangeError('Country outside selected scope')
    clauses.push(`country_code = '${country}'`)
  }
  if (maxPrice !== '') {
    const limit = Number(maxPrice)
    if (!Number.isFinite(limit) || limit <= 0 || limit > 1e9) throw new RangeError('Invalid maximum asking price')
    clauses.push(`price_amount_eur <= ${limit}`)
  }
  return clauses.join(' && ')
}

export function classicReferenceFilter(car, now = new Date()) {
  const year = Number(car.year)
  if (!Number.isInteger(year) || year < 1886 || !car.make || !car.model) throw new RangeError('Missing car identity')
  // Restrict by year before paging, but this is not a generation/condition match.
  return [`make = ${literal(car.make)}`, `model = ${literal(car.model)}`,
    `year >= ${year - 2}`, `year <= ${year + 2}`,
    `observed_at >= '${formatUtcForPocketBase(new Date(now.getTime() - 7 * 86400000))}'`,
    `observed_at <= '${formatUtcForPocketBase(now)}'`, 'price > 0'].join(' && ')
}

export function safeResearchUrl(value) {
  try {
    const url = new URL(value)
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : null
  } catch { return null }
}

const fuel = value => ({ benzine: 'petrol', gasoline: 'petrol', benzin: 'petrol', diesel: 'diesel' }[String(value || '').toLowerCase()] || String(value || '').toLowerCase())
const specificationKey = value => {
  if (typeof value !== 'string') return null
  const key = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase()
  return !key || ['unknown', 'onbekend', 'other', 'overig', 'na', 'n/a', 'none', 'null', '-'].includes(key) ? null : key
}
const gearbox = value => {
  const key = specificationKey(value)
  return ({ handgeschakeld: 'manual', handmatig: 'manual', manual: 'manual',
    automaat: 'automatic', automatic: 'automatic', automatisch: 'automatic',
    'semi-automaat': 'semi-automatic', 'semi-automatic': 'semi-automatic' })[key] || null
}
const body = value => {
  const key = specificationKey(value)
  return ({ cabrio: 'cabriolet', convertible: 'cabriolet' })[key] || key
}
export function matchedReferenceContext(car, rows, { matchSpecification = true } = {}) {
  const hp = Number(car.horsepower_hp)
  const fuelKey = fuel(car.fuel_type)
  const matches = rows.filter(row => {
    const e = row.evidence || {}
    const refHp = Number(e.horsepower_hp)
    if (fuelKey && fuel(e.fuel_normalized || e.fuel_type) !== fuelKey) return false
    if (hp > 0 && (!(refHp > 0) || Math.abs(refHp - hp) > Math.max(10, hp * .1))) return false
    return Number(row.price) > 0 && Number(row.year) >= Number(car.year) - 2 && Number(row.year) <= Number(car.year) + 2
  })
  const unique = [...new Map(matches.map(row => [row.evidence?.listing_id || row.id, row])).values()]
  const targetBody = body(car.car_type), targetGearbox = gearbox(car.transmission)
  const selected = [], otherRows = []
  // CLASSIC-SPEC-1: equal names and power do not make a coupe and cabriolet
  // interchangeable. Use stated specifications, never invent generation or
  // condition equivalence. Unmatched evidence remains inspectable, not a reject.
  for (const row of unique) {
    const e = row.evidence || {}, reasons = []
    const refBody = body(e.body_normalized) || body(e.car_type)
    const refGearbox = gearbox(e.transmission_normalized) || gearbox(e.transmission)
    if (matchSpecification && targetBody && refBody !== targetBody) reasons.push(refBody ? 'body_mismatch' : 'body_unknown')
    if (matchSpecification && targetGearbox && refGearbox !== targetGearbox) reasons.push(refGearbox ? 'gearbox_mismatch' : 'gearbox_unknown')
    if (reasons.length) otherRows.push({ row, reasons })
    else selected.push(row)
  }
  return { rows: selected, otherRows, engineMatched: hp > 0, fuelMatched: Boolean(fuelKey),
    bodyMatched: matchSpecification && Boolean(targetBody),
    gearboxMatched: matchSpecification && Boolean(targetGearbox) }
}

// Zero/blank odometers on a decades-old advert are not evidence of unused cars.
export function researchMileage(value) {
  const km = Number(value)
  return Number.isFinite(km) && km > 0 ? km : null
}

/** Observations only: no fitted depreciation line or kilometre-adjusted valuation. */
export function classicChartContext(car, rows, axis = 'mileage') {
  if (!['mileage', 'year'].includes(axis)) throw new RangeError('Invalid chart axis')
  const points = rows.map(row => ({ row, x: axis === 'mileage' ? researchMileage(row.evidence?.mileage_km) : Number(row.year) }))
    .filter(point => point.x !== null && Number.isFinite(point.x))
  const targetX = axis === 'mileage' ? researchMileage(car.mileage_km) : Number(car.year)
  const domain = axis === 'mileage'
    ? [0, Math.max(10000, Math.ceil(Math.max(targetX || 0, ...points.map(p => p.x)) / 10000) * 10000)]
    : [Number(car.year) - 2, Number(car.year) + 2]
  return { points, targetX, domain, omitted: rows.length - points.length }
}

/** ENTRY-1: the newest model year the pricer treats as a classic. pricer_core.CLASSIC_AGE_YEARS is
 * 25 and the rule is age > 25, so in 2026 that is 2000 and older. */
export const classicMaxYear = (now = new Date()) => now.getFullYear() - 26
